// A tap on a control of the windows, the menu and the bars clicks it when the finger lifts (owner 2026-10-24: Play in
// the Games window took no tap while a Mahjong deal ran; the iPad's tap log showed pointerdown and pointerup on the
// button and no click). Safari on iOS holds the click back when content shows or hides on the page during the tap (it
// takes the tap for a hover), and a deal shows and hides cards all the time. So a touch that starts and ends on the
// same control, without moving, clicks it at once; the click Safari may still send for that touch is dropped.
const CONTROL = 'input[type="button"], input[type="submit"], button, a[href], [role="button"], .lbx_close, .menu_action';
// .ht: the Kierki table's cards and buttons (hearts/HeartsTable.tsx; owner 2026-10-26, iPad: after the first trick the
// taps on the hand did nothing while the computer players' cards came and went)
// .mj_shapes: the Games window's Mahjong shape list, a layer over the page outside the window (GamesWindow.tsx)
const AREA = '.lbx_window, #menu_content, #topbar, .topbar, #footer_menu_left, #footer_privacy, .ht, .mj_shapes';
/** A touch that moved farther is a scroll or a swipe, not a tap. */
const SLOP_PX = 10;
/** How long after our click a native click on the same control is taken for its double: every touch tap is clicked
 *  here, so a native click after a touch is always that tap's double; Safari may send it only when the next tap starts
 *  (owner 2026-10-26: at 800 ms the 2nd or 3rd quick card of the Kierki pass sometimes dropped the one before it). */
const DOUBLE_MS = 3000;

/** After a touch tap we clicked, any native click this soon is that tap's own (owner 2026-10-26, iPad: quick taps on
 *  the Kierki hand still dropped the first card: Safari moves a tap's point toward the nearest control, so a late
 *  click could match the wrong tap by place, and the first card's own late click then went through). */
const AFTER_TAP_MS = 1500;
/** A touchend this soon after our click is the same touch's (pointerup and touchend come from one finger lift). */
const SAME_TOUCH_MS = 60;
/** A native click this soon after a touch we did not click (it moved) is that touch's own: never dropped. */
const OWN_CLICK_MS = 600;

/** `?taplog=1`: a small panel lists what the browser sends (touches, our clicks, the native clicks and what was done
 *  with them), for a screenshot from a real iPad. */
const tapLog = (() => {
  try {
    if (!/[?&]taplog=1\b/.test(location.search)) return null;
  } catch {
    return null;
  }
  const lines: string[] = [];
  let box: HTMLElement | null = null;
  const t0 = performance.now();
  return (line: string) => {
    if (!box) {
      box = document.createElement('pre');
      Object.assign(box.style, { position: 'fixed', left: '4px', bottom: '60px', zIndex: '2147483647', margin: '0', padding: '6px 8px', maxWidth: '96vw', background: 'rgb(0 0 0 / 78%)', color: '#9f9', font: '11px/1.35 ui-monospace, Menlo, monospace', pointerEvents: 'none', whiteSpace: 'pre-wrap' });
      document.body.append(box);
    }
    lines.push(`${String(Math.round(performance.now() - t0)).padStart(6)} ${line}`);
    if (lines.length > 16) lines.shift();
    box.textContent = lines.join('\n');
  };
})();
const label = (t: EventTarget | null): string => {
  if (!(t instanceof Element)) return '-';
  const c = t.closest<HTMLElement>('[data-card]');
  return c ? `card ${c.dataset.card}` : (t.closest<HTMLElement>('[id]')?.id ?? t.tagName.toLowerCase());
};

const controlAt = (t: EventTarget | null): HTMLElement | null => {
  if (!(t instanceof Element)) return null;
  const el = t.closest<HTMLElement>(CONTROL);
  return el && el.closest(AREA) && !(el as HTMLInputElement).disabled ? el : null;
};

export function installTapClick(): void {
  let down: { id: number; el: HTMLElement; x: number; y: number } | null = null;
  // every recent tap we clicked (quick taps on several controls: the Kierki hand, owner 2026-10-26: Safari's late click
  // of the first card came after the second tap, was taken for a new tap and dropped the first card again)
  let clicked: { el: HTMLElement; at: number; x: number; y: number }[] = [];
  // the last touch that ended: clicked by us or not (a touch we did not click, e.g. one that moved a little, keeps its
  // native click)
  let lastTouch: { at: number; ours: boolean } | null = null;
  document.addEventListener(
    'pointerdown',
    (e) => {
      down = e.pointerType === 'touch' && e.isPrimary ? (controlAt(e.target) ? { id: e.pointerId, el: controlAt(e.target)!, x: e.clientX, y: e.clientY } : null) : null;
      // the touch we click sends no click of its own at all (owner 2026-10-27, iPad: a tap on a Mahjong shape closed the
      // list on the lift, and Safari's late click landed on the ad that had been under the list: a click inside an ad's
      // frame never reaches this page, so dropping it below cannot stop it; an ad clicked by mistake can get the ad
      // account blocked). A cancelled touchend makes no click. The listener sits on the touched element itself: the
      // touchend goes to it even when our click has already taken it off the page (the shape list closes), and a
      // removed element's events never reach the document.
      if (down && e.target instanceof EventTarget) {
        const d = down;
        const end = (te: Event) => {
          const now = performance.now();
          const pt = (te as TouchEvent).changedTouches?.[0];
          // pointerup came first and clicked, or it comes next and will: the touch is still a tap on its control
          const ours = !!lastTouch?.ours && now - lastTouch.at <= SAME_TOUCH_MS;
          const tapping = down === d && !!pt && Math.hypot(pt.clientX - d.x, pt.clientY - d.y) <= SLOP_PX && controlAt(document.elementFromPoint(pt.clientX, pt.clientY)) === d.el;
          if (te.cancelable && (ours || tapping)) {
            te.preventDefault();
            tapLog?.('touchend: no native click');
          }
        };
        e.target.addEventListener('touchend', end, { once: true, passive: false });
      }
      tapLog?.(`down ${e.pointerType} ${label(e.target)} ${Math.round(e.clientX)},${Math.round(e.clientY)}`);
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    'pointercancel',
    (e) => {
      down = null;
      tapLog?.(`cancel ${e.pointerType}`);
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    'pointerup',
    (e) => {
      const d = down;
      down = null;
      if (e.pointerType === 'touch') lastTouch = { at: performance.now(), ours: false };
      if (!d || e.pointerId !== d.id || Math.hypot(e.clientX - d.x, e.clientY - d.y) > SLOP_PX) return tapLog?.(`up ${e.pointerType} ${label(e.target)}: not a tap`);
      const under = controlAt(document.elementFromPoint(e.clientX, e.clientY));
      if (under !== d.el || !d.el.isConnected) return tapLog?.(`up ${label(e.target)}: left the control`);
      const now = performance.now();
      clicked = [...clicked.filter((c) => now - c.at <= DOUBLE_MS), { el: d.el, at: now, x: e.clientX, y: e.clientY }];
      lastTouch = { at: now, ours: true };
      tapLog?.(`up -> OUR click ${label(d.el)}`);
      d.el.click();
    },
    { capture: true, passive: true },
  );
  // the native click of that same touch, if Safari sends it after all: not a second click; also when the control is
  // gone by then (Play closed its window) and the click would land on what lay under the window
  document.addEventListener(
    'click',
    (e) => {
      if (!e.isTrusted) return;
      const now = performance.now();
      clicked = clicked.filter((c) => now - c.at <= DOUBLE_MS);
      const i = clicked.findIndex((c) => Math.hypot(e.clientX - c.x, e.clientY - c.y) <= SLOP_PX * 2 || controlAt(e.target) === c.el || (e.target instanceof Node && c.el.contains(e.target)));
      const afterOurTap = !!lastTouch?.ours && now - lastTouch.at <= AFTER_TAP_MS && !!controlAt(e.target);
      // right after a touch we did not click (it moved a little): this is that touch's own click, let it through
      const ownOfLastTouch = !!lastTouch && !lastTouch.ours && now - lastTouch.at <= OWN_CLICK_MS;
      if ((i < 0 && !afterOurTap) || ownOfLastTouch) return tapLog?.(`native click ${label(e.target)} ${Math.round(e.clientX)},${Math.round(e.clientY)}: PASSED`);
      if (i >= 0) clicked.splice(i, 1);
      tapLog?.(`native click ${label(e.target)} ${Math.round(e.clientX)},${Math.round(e.clientY)}: dropped (${i >= 0 ? 'matched' : 'after our tap'})`);
      e.preventDefault();
      e.stopImmediatePropagation();
    },
    { capture: true },
  );
}
